import { useState, useEffect, useCallback } from 'react';
import { promocionService } from 'services/promocionService';
import { EstadoPromocionResponse, CalcularPromocionPayload, PromocionManualPayload } from 'types/promocion.types';
import { HttpError } from 'utils/httpClient';

export const usePromocion = (params: { gradoId: number | ''; seccionId: number | ''; anioEscolarId: number | '' } | null) => {
  const [estados, setEstados]   = useState<EstadoPromocionResponse[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [calculating, setCalc]  = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);

  // Payload pendiente de forzar: se rellena cuando el backend devuelve
  // 400 con "aún no ha concluido". La página muestra el diálogo de confirmación.
  const [pendienteForzar, setPendienteForzar] = useState<{
    payload: CalcularPromocionPayload;
    mensaje: string;
  } | null>(null);

  const cargar = useCallback(async () => {
    if (!params?.anioEscolarId || !params.gradoId || !params.seccionId) return;
    setLoading(true); setError(null);
    try {
      setEstados(await promocionService.getOrdenMerito({
        gradoId: Number(params.gradoId),
        seccionId: Number(params.seccionId),
        anioEscolarId: Number(params.anioEscolarId),
      }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar estados');
    } finally { setLoading(false); }
  }, [params?.gradoId, params?.seccionId, params?.anioEscolarId]);

  useEffect(() => { cargar(); }, [cargar]);

  const calcular = async (payload: CalcularPromocionPayload) => {
    setCalc(true); setCalcError(null); setPendienteForzar(null);
    try {
      setEstados(await promocionService.calcular(payload));
    } catch (e: unknown) {
      if (
        e instanceof HttpError &&
        e.status === 400 &&
        e.message.includes('aún no ha concluido')
      ) {
        // El backend indica que el período no terminó → guardar para diálogo
        setPendienteForzar({ payload, mensaje: e.message });
      } else {
        setCalcError(e instanceof Error ? e.message : 'Error al calcular promoción');
      }
    } finally { setCalc(false); }
  };

  // Reenvía el mismo payload con forzar: true (solo DIRECTOR debe poder llamarlo)
  const forzarCalculo = async () => {
    if (!pendienteForzar) return;
    setPendienteForzar(null);
    await calcular({ ...pendienteForzar.payload, forzar: true });
  };

  const cancelarForzar = () => setPendienteForzar(null);

  const registrarManual = async (payload: PromocionManualPayload) => {
    setCalc(true); setCalcError(null);
    try {
      const updated = await promocionService.registrarManual(payload);
      setEstados(prev => {
        const idx = prev.findIndex(e => e.matriculaId === updated.matriculaId);
        return idx >= 0 ? prev.map((e, i) => i === idx ? updated : e) : [...prev, updated];
      });
    } catch (e: unknown) {
      setCalcError(e instanceof Error ? e.message : 'Error al registrar estado');
    } finally { setCalc(false); }
  };

  return {
    estados, loading, error, calculating, calcError,
    pendienteForzar, forzarCalculo, cancelarForzar,
    calcular, registrarManual, recargar: cargar,
  };
};
